// src/utils/jobContractLedger.js
// ===========================================================================
// Job Money: change orders, total adjustments and linked work orders.
//
// One Firestore doc per contract, keyed by the SAME contract id the billing
// flow already uses (billingFlow.getContractIdForJob):
//   proposal jobs      -> the proposal id      (PJMC-10025-2, -3 ... share it)
//   ordinary jobs      -> "wo:<stem>"          (5456, 5456-2 ... share it)
//
//   jobContracts/{docId}
//     contractId        the contract id above
//     ownerWO           WO the ledger was first opened from (for labels)
//     ownerJobId
//     proposalId        when the contract came from a proposal
//     originalTotal     the approved / original total. Only changed by
//                       "Correct original total", which logs originalHistory.
//     originalSource    "proposal" | "job"
//     originalHistory   [{ id, from, to, reason, by, at }] corrections to the
//                       original (typos, archive clean-up). Proposal untouched.
//     changeOrders      [{ id, number ("CO-1"), description, amount (+/-),
//                          date, reference, addedBy, addedAt,
//                          voided, voidedBy, voidedAt, voidReason }]
//     adjustments       [{ id, amount (+/-, the change), fromTotal, toTotal,
//                          reason, date, addedBy, addedAt, voided ... }]
//     linkedWOs         ["4410", "PJMC-10019", ...]  (combined total only;
//                          they never change this contract's balance)
//     updatedAt
//
//   current contract total = originalTotal + change orders + adjustments
//
// The approved proposal is never touched. Billing (deposit / progress /
// final) reads the current total from here, so the remaining balance follows
// change orders automatically. Sent and Paid invoices are never rewritten.
// ===========================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";

export const JOB_CONTRACTS = "jobContracts";

/* ------------------------------------------------------------------------ */
/* Pure helpers (no Firestore) — unit tested                                */
/* ------------------------------------------------------------------------ */

export const money = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export const r2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

/** Firestore ids cannot contain "/". */
export const contractDocId = (contractId) =>
  String(contractId || "").replace(/\//g, "_").trim();

const active = (list) => (Array.isArray(list) ? list.filter((x) => x && !x.voided) : []);

/** Where a contract's total stands. Accepts the jobContracts doc (or null). */
export const computeContractTotals = (ledger, fallbackOriginal = 0) => {
  const original = r2(
    ledger && ledger.originalTotal !== undefined && ledger.originalTotal !== null && ledger.originalTotal !== ""
      ? money(ledger.originalTotal)
      : money(fallbackOriginal)
  );
  const changeOrders = r2(active(ledger?.changeOrders).reduce((s, c) => s + money(c.amount), 0));
  const adjustments = r2(active(ledger?.adjustments).reduce((s, a) => s + money(a.amount), 0));
  return {
    original,
    changeOrders,
    adjustments,
    current: r2(original + changeOrders + adjustments),
    changeOrderCount: active(ledger?.changeOrders).length,
  };
};

/** True once someone has added a change order or adjustment. */
export const ledgerChangesTotal = (ledger) =>
  active(ledger?.changeOrders).length > 0 || active(ledger?.adjustments).length > 0;

/**
 * True when Job Money holds the job's total: a change order, an adjustment,
 * or a corrected original. A ledger that only lists linked WOs does NOT own
 * the total, so Add Total / Change Total keep working as before.
 */
export const ledgerOwnsTotal = (ledger) =>
  ledgerChangesTotal(ledger) ||
  (Array.isArray(ledger?.originalHistory) && ledger.originalHistory.length > 0);

/** "CO-3" for the next change order. Voided ones keep their number. */
export const nextChangeOrderNumber = (ledger) => {
  const nums = (ledger?.changeOrders || [])
    .map((c) => Number(String(c.number || "").replace(/[^0-9]/g, "")))
    .filter((n) => Number.isFinite(n));
  return `CO-${(nums.length ? Math.max(...nums) : 0) + 1}`;
};

/** Normalized, de-duplicated WO list. */
export const normalizeWOs = (list = []) => {
  const seen = new Set();
  const out = [];
  list.forEach((raw) => {
    const wo = String(raw || "").trim();
    if (!wo) return;
    const key = wo.toUpperCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(wo);
  });
  return out;
};

/**
 * Money summary for one contract plus its linked work orders.
 *   own:    { total, billed, paid }
 *   linked: [{ wo, total, billed, paid, ... }]
 */
export const combineTotals = (own, linked = []) => {
  const sum = (key) => r2(linked.reduce((s, l) => s + money(l[key]), 0));
  const total = r2(money(own.total) + sum("total"));
  const billed = r2(money(own.billed) + sum("billed"));
  const paid = r2(money(own.paid) + sum("paid"));
  return {
    total,
    billed,
    paid,
    leftToBill: r2(total - billed),
    owed: r2(billed - paid),
    unpaidTotal: r2(total - paid),
  };
};

/* ------------------------------------------------------------------------ */
/* Firestore                                                                */
/* ------------------------------------------------------------------------ */

const whoAmI = () => {
  const u = getAuth().currentUser;
  return u?.displayName || u?.email || "";
};

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const loadContractLedger = async (contractId) => {
  const id = contractDocId(contractId);
  if (!id) return null;
  try {
    const snap = await getDoc(doc(db, JOB_CONTRACTS, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.warn("Could not load job contract ledger:", err);
    return null;
  }
};

/**
 * The ledger for a contract, creating it on first use with the original
 * total. `originalTotal` is only used when the doc does not exist yet.
 */
export const ensureContractLedger = async ({ contractId, job, originalTotal, originalSource }) => {
  const existing = await loadContractLedger(contractId);
  if (existing) return existing;

  const id = contractDocId(contractId);
  const data = {
    contractId,
    ownerWO: job?.wo ? String(job.wo) : "",
    ownerJobId: job?.id || "",
    siteName: job?.siteName || "",
    proposalId: job?.proposalId || job?.contractBilling?.proposalId || "",
    originalTotal: r2(money(originalTotal)),
    originalSource: originalSource || "job",
    changeOrders: [],
    adjustments: [],
    linkedWOs: [],
    createdBy: whoAmI(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, JOB_CONTRACTS, id), data, { merge: true });
  return { id, ...data };
};

const saveLedgerFields = async (ledger, fields) => {
  await updateDoc(doc(db, JOB_CONTRACTS, ledger.id), { ...fields, updatedAt: serverTimestamp() });
  return { ...ledger, ...fields };
};

export const addChangeOrder = async (ledger, { description, amount, date, reference }) => {
  const entry = {
    id: newId(),
    number: nextChangeOrderNumber(ledger),
    description: String(description || "").trim(),
    amount: r2(money(amount)),
    date: date || new Date().toISOString().slice(0, 10),
    reference: String(reference || "").trim(),
    addedBy: whoAmI(),
    addedAt: new Date().toISOString(),
    voided: false,
  };
  return saveLedgerFields(ledger, { changeOrders: [...(ledger.changeOrders || []), entry] });
};

/**
 * Fixes the ORIGINAL total itself (a typo, or cleaning up an old job).
 * Change orders and adjustments stay on top of it. Logged, never silent.
 */
export const correctOriginalTotal = async (ledger, { newOriginal, reason }) => {
  const from = r2(money(ledger.originalTotal));
  const to = r2(money(newOriginal));
  const entry = {
    id: newId(),
    from,
    to,
    reason: String(reason || "").trim(),
    by: whoAmI(),
    at: new Date().toISOString(),
  };
  return saveLedgerFields(ledger, {
    originalTotal: to,
    originalHistory: [...(ledger.originalHistory || []), entry],
  });
};

/**
 * A ledger that does not own the total yet (link-only) follows the job's
 * live total. Call before the first change order / adjustment / correction
 * so the original that gets fixed in place is today's number.
 */
export const syncOriginalIfFollowing = async (ledger, liveOriginal) => {
  if (!ledger || ledgerOwnsTotal(ledger)) return ledger;
  const live = r2(money(liveOriginal));
  if (live === r2(money(ledger.originalTotal))) return ledger;
  return saveLedgerFields(ledger, { originalTotal: live });
};

/** Sets the current total to `newTotal` by recording the difference. */
export const addTotalAdjustment = async (ledger, { newTotal, reason, date }) => {
  const before = computeContractTotals(ledger).current;
  const target = r2(money(newTotal));
  const entry = {
    id: newId(),
    amount: r2(target - before),
    fromTotal: before,
    toTotal: target,
    reason: String(reason || "").trim(),
    date: date || new Date().toISOString().slice(0, 10),
    addedBy: whoAmI(),
    addedAt: new Date().toISOString(),
    voided: false,
  };
  return saveLedgerFields(ledger, { adjustments: [...(ledger.adjustments || []), entry] });
};

/** Voids (never deletes) a change order or adjustment, keeping the record. */
export const voidLedgerEntry = async (ledger, listKey, entryId, reason = "") => {
  const list = (ledger[listKey] || []).map((e) =>
    e.id === entryId
      ? { ...e, voided: true, voidedBy: whoAmI(), voidedAt: new Date().toISOString(), voidReason: reason }
      : e
  );
  return saveLedgerFields(ledger, { [listKey]: list });
};

export const setLinkedWOs = async (ledger, wos) =>
  saveLedgerFields(ledger, { linkedWOs: normalizeWOs(wos) });

/** Contracts that list this WO as linked (shown as "Linked from"). */
export const findContractsLinkingWO = async (wo) => {
  const key = String(wo || "").trim();
  if (!key) return [];
  try {
    const snap = await getDocs(
      query(collection(db, JOB_CONTRACTS), where("linkedWOs", "array-contains", key))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("Linked-from lookup failed:", err);
    return [];
  }
};

/* ------------------------------------------------------------------------ */
/* Money lookups                                                            */
/* ------------------------------------------------------------------------ */

/** Payments recorded on the Deposits page against these invoice numbers. */
export const loadPaymentsForWOs = async (wos = []) => {
  const list = normalizeWOs(wos);
  const byId = {};
  await Promise.all(
    list.map(async (wo) => {
      try {
        const snap = await getDocs(query(collection(db, "deposits"), where("invoiceNumber", "==", wo)));
        snap.docs.forEach((d) => {
          byId[d.id] = { id: d.id, ...d.data() };
        });
      } catch (err) {
        console.warn("Payment lookup failed for", wo, err);
      }
    })
  );
  return Object.values(byId);
};

export const loadInvoicesForWO = async (wo) => {
  const key = String(wo || "").trim();
  if (!key) return [];
  try {
    const snap = await getDocs(query(collection(db, "approvedInvoices"), where("wo", "==", key)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("Invoice lookup failed for", key, err);
    return [];
  }
};

export const loadJobsForWO = async (wo) => {
  const key = String(wo || "").trim();
  if (!key) return [];
  try {
    const snap = await getDocs(query(collection(db, "jobs"), where("wo", "==", key)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("Job lookup failed for", key, err);
    return [];
  }
};

/* ------------------------------------------------------------------------ */
/* Keep open invoices in step after the total changes                       */
/* ------------------------------------------------------------------------ */

/**
 * After a change order / adjustment: give Draft and Reviewed deposit,
 * progress and final invoices on this contract the new total and refresh
 * their balance lines. Sent and Paid invoices are left exactly as they are.
 * Jobs carrying this contract get the new total too, so a Continue copies it.
 *
 * `refreshHeader` is billingFlow.getRefreshedHeaderPatch, passed in to keep
 * this file free of a circular import.
 */
export const pushNewTotalToOpenInvoices = async ({ contractId, newTotal, invoices, refreshHeader }) => {
  const total = r2(newTotal);
  const ledgerKinds = ["deposit", "progress", "final"];
  const updated = invoices.map((inv) => {
    const status = String(inv.status || "Draft").toLowerCase();
    const open = status !== "sent" && status !== "paid";
    if (open && ledgerKinds.includes(inv.billingKind)) return { ...inv, contractTotal: total };
    return inv;
  });

  let changed = 0;
  for (const inv of updated) {
    const original = invoices.find((i) => i.id === inv.id);
    if (inv.contractTotal === original.contractTotal) continue;
    const patch = { contractTotal: total, ...(refreshHeader ? refreshHeader(inv, updated) || {} : {}) };
    try {
      await updateDoc(doc(db, "approvedInvoices", inv.id), { ...patch, updatedAt: new Date().toISOString() });
      changed += 1;
    } catch (err) {
      console.warn("Could not update open invoice", inv.id, err);
    }
  }

  try {
    const jobsSnap = await getDocs(
      query(collection(db, "jobs"), where("contractBilling.contractId", "==", contractId))
    );
    await Promise.all(
      jobsSnap.docs.map((d) =>
        updateDoc(d.ref, { "contractBilling.contractTotal": total }).catch((e) =>
          console.warn("Could not update job contract total", d.id, e)
        )
      )
    );
  } catch (err) {
    console.warn("Could not update jobs on this contract:", err);
  }

  return changed;
};
